import { Video, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ContinueVibeNudgeProps {
  matchSource: string;
  profileName: string;
  contextDisplay?: string | null;
  onVideoChat: () => void;
  onDismiss: () => void;
  className?: string;
}

export const ContinueVibeNudge = ({
  matchSource,
  profileName,
  contextDisplay,
  onVideoChat,
  onDismiss,
  className,
}: ContinueVibeNudgeProps) => {
  // Only show for exhibition or global_connect matches
  if (matchSource !== "exhibition" && matchSource !== "global_connect") {
    return null;
  }

  const isExhibition = matchSource === "exhibition";
  const emoji = isExhibition ? "🎪" : "🌍";
  const actionText = isExhibition ? "Keep the festival vibe going!" : "Bridge the distance!";

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl p-4",
        "bg-gradient-to-r",
        isExhibition
          ? "from-amber-500/15 via-orange-500/10 to-rose-500/15"
          : "from-blue-500/15 via-cyan-500/10 to-teal-500/15",
        "border",
        isExhibition ? "border-amber-500/30" : "border-blue-500/30",
        "animate-fade-in",
        className
      )}
    >
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 transition-colors"
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </button>

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
            isExhibition ? "bg-amber-500/20" : "bg-blue-500/20"
          )}
        >
          <span className="text-xl">{emoji}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkles className={cn("w-4 h-4", isExhibition ? "text-amber-500" : "text-blue-500")} />
            <p className="text-sm font-semibold">Continue the Vibe</p>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            You met {profileName} {contextDisplay ? `at ${contextDisplay}` : ""}. {actionText}
          </p>

          {/* CTA */}
          <Button
            size="sm"
            onClick={onVideoChat}
            className={cn(
              "mt-3 gap-2",
              isExhibition
                ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                : "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            )}
          >
            <Video className="w-4 h-4" />
            Start Video Chat
          </Button>
        </div>
      </div>
    </div>
  );
};
